/**
 * FirmaEgitimi.jsx — firma portalının kullanım turu ve üst çubuktaki "?" düğmesi (2026-10-28; bağlama dayalı, 2026-10-30).
 *
 * Katılımcı firma girişlerine, ekranda ne açıksa onu Shepherd balonlarıyla ve vurgularla anlatır. Tur sabit bir sıra DEĞİLDİR: her
 * bağlamın (bir sayfa, Firma Kartı'nın bir sekmesi, onay ekranı, açık bir pencere) kendi anlatımı vardır. Hangi bağlamda olunduğunu
 * services/egitimTuru.js'teki `baglamBul` saf işlevi bulur (adım listesi, kime gösterildiği ve ne zaman kendiliğinden başladığı da
 * orada, jest ile denenir); burası ekran durumunu okur (açık pencere, etkin sekme, adres, onay geçidi) ve Shepherd'ı sürer.
 *
 * Olaylara göre çalışır: "?" düğmesi o an açık olanın anlatımını başlatır; tur açıkken bağlam değişirse (pencere açıldı ya da
 * kapandı, sekme ya da sayfa değişti — DOM'u gözleyen bir MutationObserver ve adres değişimi bunu yakalar) tur yeni bağlamın
 * anlatımına geçer, anlatımı olmayan bir ekrana (işaretsiz pencere dahil) gelinirse kapanır. Tur kendi başına sayfa değiştirmez:
 * bir sayfanın anlatımı bitince sıradaki menü öğesini ya da sekmeyi gösterip kullanıcının tıklamasını bekler ("Oraya git" düğmesi
 * aynı tıklamayı yapar). Tur açıkken vurgulanan öğenin dışı tıklanamaz (Shepherd'ın örtüsü); vurgulanan öğe gerçekten çalışır, yani
 * ona tıklamak işi yapar ve bağlam değişirse tur ona uyar. Balondaki × her an kapatır; kapanan tur "?" ile açılana kadar kapalı kalır.
 * Pencere açıkken üst çubuk Bootstrap'in perdesinin altında kalır; o yüzden anlatımı olan bir pencere açık ve tur kapalıyken
 * pencerenin üstünde yüzen bir "?" düğmesi çizilir.
 *
 * Açıp kapatma: personelin Kayıt Ayarları'ndaki anahtarı (`kayit.portal_egitim`, varsayılan KAPALI) kapalıyken bu bileşen HİÇBİR ŞEY
 * çizmez ve başlatmaz. Açıkken: onay geçidi ekrana gelince onay anlatımı, ilk girişte Ana Sayfa'da portal anlatımı kendiliğinden bir
 * kez başlar (kullanıcıya göre hatırlanır).
 *
 * Sayfalar hedef öğeyi `data-tour="..."` ile, pencereler bağlamını `data-tour-baglam="..."` ile işaretler. Hedef bulunamazsa (boş liste,
 * dar ekranda gizli menü, yetkisiz düğme) adım ekranın ortasında gösterilir; tur hiçbir veri durumunda kopmaz. Bileşen Header'da
 * durur, yani sayfa değişse de yaşar.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Shepherd from 'shepherd.js';
import { usePortal } from '../Contexts/PortalContext';
import { useOnay } from '../Contexts/OnayContext';
import { getPortalEgitimAcik } from '../../services/systemSettingsService';
import {
  BAGLAM, BELIRSIZ, TUR_ONAY, YOK_ADIMI, baglamBul, egitimAdimlari, egitimGorebilir, egitimGoruldu, egitimGoruldusuYaz,
  egitimKendiligindenBaslar, onayBaglamiMi, onayTuruKendiligindenBaslar, sayfayaGidilmeli,
} from '../../services/egitimTuru';

const BEKLEME_MS = 5000;
const OTO_BASLAMA_PENCERESI_MS = 30000;
const KONTROL_MS = 250;
const SEKME_ONEKI = 'firmakart-sekme-';

const bekle = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

/** Ekranda gerçekten görünen öğe mi (boyutu var, yatayda ekranın içinde: dar ekranda yandan kapalı menü sayılmaz). */
function gorunur(el) {
  if (!el) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0 && r.right > 0 && r.left < window.innerWidth && getComputedStyle(el).visibility !== 'hidden';
}

const yukleniyor = () => gorunur(document.querySelector('.page-content .spinner-border'));
const ekrandaVar = (secici) => Array.from(document.querySelectorAll(secici)).some(gorunur);

/**
 * Ekran durumu: en üstteki açık pencere (ve `data-tour-baglam` işareti) ile Firma Kartı'nın etkin sekmesi. Anlatımı olmayan
 * bir pencere de "pencere açık" sayılır: arkasındaki sayfanın anlatımı o pencerenin altında gösterilmesin.
 */
function ekranDurumu() {
  const pencereler = Array.from(document.querySelectorAll('.modal.show')).filter(gorunur);
  const ust = pencereler[pencereler.length - 1];
  const modal = ust?.querySelector('[data-tour-baglam]')?.getAttribute('data-tour-baglam') ?? null;
  const sekmeEl = document.querySelector(`[data-tour^="${SEKME_ONEKI}"].active`);
  const sekme = sekmeEl ? sekmeEl.getAttribute('data-tour').slice(SEKME_ONEKI.length) : null;
  return { modalAcik: Boolean(ust), modal, sekme };
}

/**
 * Hedef öğeyi bekler. İlk seçici asıl hedeftir; ardından gelenler yedektir ve yalnızca sayfa yüklenmeyi bitirince
 * (görünen spinner kalmayınca) ya da süre dolunca kabul edilir — yoksa yedek, asıl hedeften önce yakalanırdı.
 * Bulunan öğeyi ve hangi seçiciden geldiğini ({ el, i }) döner; hiçbiri yoksa null (adım ortada gösterilir).
 */
async function hedefiBekle(hedefler) {
  if (!hedefler.length) return null;
  const bitis = Date.now() + BEKLEME_MS;
  for (;;) {
    let yedek = null;
    for (let i = 0; i < hedefler.length; i += 1) {
      const el = Array.from(document.querySelectorAll(hedefler[i])).find(gorunur);
      if (el && i === 0) return { el, i };
      if (el && !yedek) yedek = { el, i };
    }
    if (yedek && (!yukleniyor() || Date.now() >= bitis)) return yedek;
    if (Date.now() >= bitis) return null;
    await bekle(150);
  }
}

export default function FirmaEgitimi() {
  const { t } = useTranslation('portal');
  const portal = usePortal();
  const onay = useOnay();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [anahtarAcik, setAnahtarAcik] = useState(false);
  const [turAcik, setTurAcik] = useState(false);
  const [yuzenGoster, setYuzenGoster] = useState(false);
  const turRef = useRef(null);
  // Çalışan turun bağlamı (null = anlatımsız ekranın "yok" adımı). Ekranın bağlamı bundan farklılaşınca tur yeni bağlama geçer.
  const turBaglamRef = useRef(null);
  // Tur sayfalar arasında dururken en güncel yol, onay durumu ve navigate'e ihtiyaç duyar; closure'lar eski değeri tutmasın.
  const navigateRef = useRef(navigate);
  const yolRef = useRef(pathname);
  const onayRef = useRef(onay?.durum);
  useEffect(() => {
    navigateRef.current = navigate;
    yolRef.current = pathname;
    onayRef.current = onay?.durum;
  });

  const gorebilir = egitimGorebilir(portal);
  const kullaniciId = localStorage.getItem('userId');
  const onayDurumu = onay?.durum;

  // Personelin anahtarı: yalnızca turu görebilecek bir girişte okunur (başkasına istek bile atılmaz). Okunamazsa KAPALI.
  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      const acik = await getPortalEgitimAcik();
      if (!ignore) setAnahtarAcik(acik);
    }
    if (gorebilir) run();
    return () => { ignore = true; };
  }, [gorebilir]);

  const goster = gorebilir && anahtarAcik;

  const durdur = useCallback(() => {
    const tur = turRef.current;
    turRef.current = null;
    turBaglamRef.current = null;
    if (tur && tur.isActive()) tur.cancel();
  }, []);

  /** Şu anki ekranın bağlamı ve pencere bilgisi (bkz. egitimTuru.baglamBul). */
  const baglamiOku = useCallback(() => {
    const ekran = ekranDurumu();
    return { ...ekran, baglam: baglamBul({ onayDurumu: onayRef.current, pathname: yolRef.current, ...ekran }) };
  }, []);

  /**
   * `baglam` verilirse o bağlamın, verilmezse (el ile "?") şu an ekranda açık olanın anlatımını başlatır. Anlatımı olmayan bir
   * ekranda tek bir "bu ekranın anlatımı yok" adımı gösterilir.
   */
  const baslat = useCallback((baglamVerilen) => {
    durdur();
    let baglam = baglamVerilen;
    if (baglam === undefined) {
      baglam = baglamiOku().baglam;
      if (baglam === BELIRSIZ) baglam = String(yolRef.current).toLowerCase() === '/firma/firmakart' ? BAGLAM.KART_BILGILER : null;
    }
    const bulunan = baglam ? egitimAdimlari(baglam, portal.seviye, portal.aktif?.kod, ekrandaVar) : [];
    const adimlar = bulunan.length ? bulunan : [YOK_ADIMI];

    const tur = new Shepherd.Tour({
      useModalOverlay: true,
      exitOnEsc: true,
      keyboardNavigation: true,
      defaultStepOptions: {
        cancelIcon: { enabled: true, label: t('egitim.kapat') },
        // Ok yok: şablonun _tour.scss'i tek bir yukarı ok çizer (her yerleşimde yukarı bakar); vurgulanan öğe zaten hedefi gösterir.
        arrow: false,
        scrollTo: { behavior: 'smooth', block: 'center' },
        modalOverlayOpeningPadding: 6,
        modalOverlayOpeningRadius: 6,
      },
    });
    // Tur kendiliğinden (× / Esc / Bitir) ya da durdur() ile kapanınca düğmenin "tur açık" durumunu güncelle; bu sırada başka bir
    // tur başlamışsa (bağlam geçişi) açık kalır.
    const bitti = () => {
      if (turRef.current === tur) {
        turRef.current = null;
        turBaglamRef.current = null;
      }
      setTurAcik(turRef.current !== null);
    };
    tur.on('cancel', bitti);
    tur.on('complete', bitti);

    const metin = (a, n) => {
      const kap = document.createElement('div');
      const p = document.createElement('p');
      p.className = 'mb-2';
      p.style.whiteSpace = 'pre-line'; // metindeki satır sonları (ör. Yetki Durumu'nun üç satırı) korunur
      p.textContent = t(`egitim.adimlar.${a.id}.metin`);
      const sayac = document.createElement('div');
      sayac.className = 'text-muted small';
      sayac.textContent = t('egitim.adim', { n, toplam: adimlar.length });
      kap.append(p, sayac);
      return kap;
    };

    // "Oraya git": tur kullanıcı yerine yalnızca isteyince gider. Adresi olan hedefe (menü öğesi) navigate eder — dar ekranda menü
    // gizli olabilir —, adresi olmayan hedefi (Firma Kartı sekmesi) tıklar. Bağlam değişince tur yeni bağlama kendisi geçer.
    const git = (a) => {
      if (a.route && sayfayaGidilmeli(a.route, yolRef.current)) {
        navigateRef.current(a.route);
        return;
      }
      for (const secici of a.hedef) {
        const el = Array.from(document.querySelectorAll(secici)).find(gorunur);
        if (el) { el.click(); return; }
      }
    };

    // Adım gösterilmeden önce: hedefi bul ve balonu ona bağla (bulunamazsa ortada kalır).
    const hazirla = async (a, adim) => {
      const bulundu = await hedefiBekle(a.hedef || []);
      const yer = Array.isArray(a.yer) ? a.yer[bulundu ? bulundu.i : 0] : a.yer;
      adim.updateStepOptions({ attachTo: bulundu ? { element: bulundu.el, on: yer || 'bottom' } : undefined });
    };

    adimlar.forEach((a, i) => {
      const sonMu = i === adimlar.length - 1;
      let ileri;
      if (a.siradaki) ileri = { text: t('egitim.git'), classes: 'btn btn-primary btn-sm', action: () => git(a) };
      else if (sonMu) ileri = { text: t('egitim.bitir'), classes: 'btn btn-primary btn-sm', action: () => tur.complete() };
      else ileri = { text: t('egitim.ileri'), classes: 'btn btn-primary btn-sm', action: () => tur.next() };
      const adim = tur.addStep({
        id: a.id,
        title: t(`egitim.adimlar.${a.id}.baslik`),
        text: metin(a, i + 1),
        buttons: [
          ...(i > 0 ? [{ text: t('egitim.geri'), classes: 'btn btn-light btn-sm', action: () => tur.back() }] : []),
          ileri,
        ],
        beforeShowPromise: () => hazirla(a, adim),
      });
    });

    turRef.current = tur;
    turBaglamRef.current = baglam ?? null;
    setTurAcik(true);
    tur.start();
  }, [portal, t, durdur, baglamiOku]);

  /**
   * Ekran değişti mi diye bakar (DOM gözlemcisi ve adres / onay değişimi çağırır). Tur açıksa ve bağlam farklıysa yeni bağlama geçer;
   * anlatımsız bir ekrana gelindiyse kapatır. Onay ekranından çıkış (geçit kalktı) açık onay anlatımını kapatır: portal anlatımı
   * Ana Sayfa'da ayrıca, bir kez kendiliğinden başlar.
   */
  const kontrolEt = useCallback(() => {
    const ekran = baglamiOku();
    setYuzenGoster(Boolean(ekran.modalAcik && ekran.baglam && ekran.baglam !== BELIRSIZ));
    const tur = turRef.current;
    if (!tur || !tur.isActive()) return;
    if (ekran.baglam === BELIRSIZ || ekran.baglam === turBaglamRef.current) return;
    if (ekran.baglam === null || (onayBaglamiMi(turBaglamRef.current) && !onayBaglamiMi(ekran.baglam))) {
      durdur();
      return;
    }
    baslat(ekran.baglam);
  }, [baglamiOku, baslat, durdur]);

  // Olay kaynağı: DOM'daki değişiklikler (pencere açılıp kapanır, sekme değişir, sayfa çizilir) ve adres / onay durumu değişimi.
  // Gözlemci tek bir zamanlayıcıyla sınırlanır (sürekli değişen bir sayfada bile yoklama aralığı en çok KONTROL_MS'de bir).
  useEffect(() => {
    if (!goster) return undefined;
    let zaman = null;
    const planla = () => {
      if (zaman) return;
      zaman = setTimeout(() => { zaman = null; kontrolEt(); }, KONTROL_MS);
    };
    const gozlemci = new MutationObserver(planla);
    gozlemci.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    planla();
    return () => {
      gozlemci.disconnect();
      if (zaman) clearTimeout(zaman);
    };
  }, [goster, kontrolEt, pathname, onayDurumu]);

  // İlk girişte kendiliğinden bir kez: anahtar açık, Katılımcı, Ana Sayfa'dayız ve bu kullanıcı turu görmedi. Onay geçidi açıkken
  // sayfa yoktur; hedef (Ana Sayfa'nın özet alanı) çıkana kadar beklenir, geçit kalkınca ve sayfa yüklenince başlar.
  // "Gördü" işareti BAŞLARKEN yazılır: bağlam yenilense de (dil, set değişimi) ikinci kez başlamaz; yeniden başlatan "?" düğmesidir.
  useEffect(() => {
    const goruldu = egitimGoruldu(kullaniciId, localStorage);
    if (!egitimKendiligindenBaslar({ anahtarAcik, portal, pathname, goruldu })) return undefined;
    if (onayDurumu === 'bekliyor' || onayDurumu === 'yukleniyor') return undefined;
    if (turRef.current && turRef.current.isActive()) return undefined;
    const yoklama = setInterval(() => {
      if (!document.querySelector('[data-tour="anasayfa-ozet"]')) return;
      clearInterval(yoklama);
      egitimGoruldusuYaz(kullaniciId, localStorage);
      if (!(turRef.current && turRef.current.isActive())) baslat(BAGLAM.ANASAYFA);
    }, 500);
    const vazgec = setTimeout(() => clearInterval(yoklama), OTO_BASLAMA_PENCERESI_MS);
    return () => { clearInterval(yoklama); clearTimeout(vazgec); };
  }, [anahtarAcik, portal, pathname, onayDurumu, kullaniciId, baslat]);

  // Onay ekranı anlatımı: geçit ekrana geldiğinde (ilk girişte, onaylanacak metin varken) bir kez kendiliğinden başlar; ayrı "gördü" işareti
  // taşır (egitimTuru.TUR_ONAY). Geçit kalkınca açık onay anlatımı kontrolEt'te kapanır; portal anlatımı Ana Sayfa'da ayrıca başlar.
  useEffect(() => {
    const goruldu = egitimGoruldu(kullaniciId, localStorage, TUR_ONAY);
    if (!onayTuruKendiligindenBaslar({ anahtarAcik, portal, onayDurumu, goruldu })) return undefined;
    if (turRef.current && turRef.current.isActive()) return undefined;
    const yoklama = setInterval(() => {
      if (!document.querySelector('[data-tour="onay-gecidi"]')) return;
      clearInterval(yoklama);
      egitimGoruldusuYaz(kullaniciId, localStorage, TUR_ONAY);
      baslat(BAGLAM.ONAY);
    }, 500);
    const vazgec = setTimeout(() => clearInterval(yoklama), OTO_BASLAMA_PENCERESI_MS);
    return () => { clearInterval(yoklama); clearTimeout(vazgec); };
  }, [anahtarAcik, portal, onayDurumu, kullaniciId, baslat]);

  // Tur görülemez hâle gelirse (anahtar kapandı, başlıktan başka sete geçildi) ya da bileşen kalkarsa kapanır.
  useEffect(() => {
    if (!goster) durdur();
  }, [goster, durdur]);
  useEffect(() => durdur, [durdur]);

  if (!goster) return null;
  return (
    <>
      <div className="ms-1 header-item">
        <button
          type="button"
          data-tour="egitim-dugmesi"
          onClick={() => baslat()}
          title={t('egitim.dugme')}
          aria-label={t('egitim.dugme')}
          className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle"
        >
          <i className="bx bx-help-circle fs-22" />
        </button>
      </div>
      {yuzenGoster && !turAcik && createPortal(
        <button
          type="button"
          onClick={() => baslat()}
          title={t('egitim.dugme')}
          aria-label={t('egitim.dugme')}
          className="egitim-yuzen-dugme btn btn-icon btn-primary rounded-circle shadow"
        >
          <i className="bx bx-help-circle fs-22" />
        </button>,
        document.body,
      )}
    </>
  );
}
